import type { SiteManifest } from './types';

/**
 * AGP (Alpha Global Prestige) site-content manifest. Defaults are the 2026
 * redesign handoff copy VERBATIM, lifted from the bespoke components in
 * `apps/member/src/tenants/agp/storefront/` — an un-edited AGP site renders
 * byte-identical to before this manifest existed.
 *
 * Image defaults are the handoff's Unsplash placeholders (client's call to go
 * live with them); the client replaces them by uploading via the admin.
 *
 * The Testimonials page's quote LIST is a special case: it was already editable
 * as storefront `company.testimonials`, so the manifest's `testimonials.quotes`
 * repeater stays inert until an admin clicks Customize — see the resolution
 * order in `apps/member/src/tenants/agp/storefront/testimonials.tsx`. That way
 * adding this group can't discard quotes AGP already entered in the old place.
 *
 * NOT here (already runtime-editable elsewhere): footer (storefront `footer` /
 * `social`), brand names (`brand`), nav labels/routes and colors/fonts
 * (structural, code-owned).
 */
export const AGP_SITE: SiteManifest = {
  tenant: 'agp',
  label: 'AGP Website',
  // AGP's home AND /storefront are fully bespoke, so the shared Home tab
  // (hero slides/banners), Official Store, Shop Page, and the Landing/Featured
  // Packages curation pages never render anywhere. Product Page stays — AGP
  // links into the shared /product/:id detail (and cart/checkout).
  adminHide: {
    storefrontTabs: ['home', 'official', 'shop'],
    sidebarItems: ['/landing-packages', '/featured-packages'],
  },
  groups: [
    {
      id: 'home',
      label: 'Home',
      sections: [
        {
          id: 'hero',
          label: 'Hero',
          fields: [
            {
              id: 'eyebrow',
              kind: 'text',
              label: 'Eyebrow',
              default: 'AGP Klair International, Incorporated',
            },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: '“Adding Life by\nSharing a Cup.”',
              hint: 'Line breaks are kept.',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                "Java Premium Green Coffee & Choco Moringga Plus — nature's finest, crafted to fuel your health and build your future.",
            },
            {
              id: 'ctaPrimary',
              kind: 'text',
              label: 'Primary button',
              default: 'Discover Our Products',
            },
            { id: 'ctaSecondary', kind: 'text', label: 'Secondary button', default: 'About AGP' },
            {
              id: 'image',
              kind: 'image',
              label: 'Hero background',
              defaultUrl:
                'https://images.unsplash.com/photo-1509785307050-d4066910ec1e?w=1600&q=80',
            },
          ],
        },
        {
          id: 'story',
          label: 'Our Story',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Our Story' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Building Health.\nCreating Opportunities.',
            },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              default: [
                'Alpha Global Prestige — Klair International is a direct-selling wellness company built on a simple but powerful belief: a single cup can change lives. For more than nine years, we have grown entirely through the genuine results of our customers — built on trust, word-of-mouth, and repeat orders.',
                'Recognized two consecutive years running for excellence in company leadership and product quality, AGP has evolved from a Philippine-born brand into a global wellness movement spanning **7+ countries worldwide.**',
              ],
            },
            {
              id: 'stats',
              kind: 'objectList',
              label: 'Stats',
              itemFields: [
                { id: 'num', kind: 'text', label: 'Number' },
                { id: 'label', kind: 'text', label: 'Label' },
              ],
              default: [
                { num: '7+', label: 'Countries' },
                { num: '9+', label: 'Years Strong' },
                { num: '10+', label: 'Awards Won' },
              ],
              maxItems: 6,
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'About Us' },
            {
              id: 'image',
              kind: 'image',
              label: 'Story image',
              defaultUrl: 'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=900&q=80',
            },
          ],
        },
        {
          id: 'pillars',
          label: 'Why AGP',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Why AGP' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Transforming Lives.\nOne Cup at a Time.',
            },
            {
              id: 'lead',
              kind: 'textarea',
              label: 'Lead paragraph',
              default:
                'We are more than a wellness brand — we are a community built on integrity, empowerment, and the belief that health and opportunity go hand in hand.',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Pillars',
              itemFields: [
                { id: 'num', kind: 'text', label: 'Number' },
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'body', kind: 'textarea', label: 'Body' },
              ],
              default: [
                {
                  num: '01',
                  title: '100% Natural',
                  body: 'Every AGP product is formulated with the finest natural ingredients — from Philippine-grown green coffee to our antioxidant-rich Choco Moringga Plus. No artificial fillers. No compromises. Pure, honest wellness in every cup.',
                },
                {
                  num: '02',
                  title: 'Health First',
                  body: 'Our mission is to make healthy living accessible to every family. Infused with antioxidants, vitamins, and minerals, our beverages are designed to boost immunity, support detoxification, and help your whole family thrive — naturally.',
                },
                {
                  num: '03',
                  title: 'Worldwide',
                  body: 'Born in the Philippines, embraced by the world. AGP products are trusted by distributors and consumers across Europe, the USA, Canada, Asia, and the Middle East — a growing global family united by one shared cup.',
                },
              ],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'awards',
          label: 'Awards strip',
          fields: [
            {
              id: 'items',
              kind: 'objectList',
              label: 'Awards',
              itemFields: [
                { id: 'eyebrow', kind: 'text', label: 'Eyebrow' },
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'source', kind: 'text', label: 'Source' },
              ],
              default: [
                {
                  eyebrow: 'Recognition',
                  name: 'Most Trusted Consumer Brand Award',
                  source: 'Two Consecutive Years',
                },
                {
                  eyebrow: 'International',
                  name: 'Global Brand Award',
                  source: 'International Recognition',
                },
                {
                  eyebrow: 'Certification',
                  name: 'FDA-Registered Products',
                  source: 'Quality & Safety Certified',
                },
              ],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'beverages',
          label: 'Beverages band',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Wellness Beverages' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Java Green Coffee &\nChoco Moringga Plus',
            },
            {
              id: 'body',
              kind: 'textarea',
              label: 'Body',
              default:
                'Discover the creative versatility of Java Green Coffee — from Java milk tea and coffee jelly to a healthy champorado. Pair it with Choco Moringga Plus, bursting with antioxidants and minerals, and enjoy guilt-free indulgence every day.',
            },
            { id: 'cta', kind: 'text', label: 'Button', default: 'View Beverages' },
            {
              id: 'image',
              kind: 'image',
              label: 'Band image',
              defaultUrl: 'https://images.unsplash.com/photo-1509785307050-d4066910ec1e?w=900&q=80',
            },
          ],
        },
        {
          id: 'values',
          label: 'Core Values',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'What We Stand For' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Our Core Values' },
            {
              id: 'lead',
              kind: 'textarea',
              label: 'Lead paragraph',
              default:
                'The principles that guide every product, every partnership, and every cup we share.',
            },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Values',
              itemFields: [
                { id: 'num', kind: 'text', label: 'Number' },
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'desc', kind: 'textarea', label: 'Description' },
              ],
              default: [
                {
                  num: '01',
                  name: 'Integrity',
                  desc: 'We do what we say, every time — in our products, our partnerships, and our promises.',
                },
                {
                  num: '02',
                  name: 'Health & Wellness',
                  desc: 'Every product is built to genuinely improve the health and lives of our customers and their families.',
                },
                {
                  num: '03',
                  name: 'Empowerment',
                  desc: 'We break financial barriers by giving every business partner a real and meaningful opportunity.',
                },
                {
                  num: '04',
                  name: 'Community',
                  desc: 'Our distributors and customers are not just partners — they are a global family growing together.',
                },
                {
                  num: '05',
                  name: 'Excellence',
                  desc: 'Award-winning standards in every product, every process, and every interaction we have.',
                },
              ],
              maxItems: 8,
            },
          ],
        },
        {
          id: 'cta',
          label: 'Call to action',
          fields: [
            {
              id: 'label',
              kind: 'text',
              label: 'Label',
              default: 'Ready to Add Life by Sharing a Cup?',
            },
            {
              id: 'title',
              kind: 'text',
              label: 'Title',
              default: 'Start Your Wellness Journey Today',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Join thousands of AGP partners and customers across 7+ countries who are building healthier lives and brighter financial futures — one cup at a time.',
            },
            {
              id: 'ctaPrimary',
              kind: 'text',
              label: 'Primary button',
              default: 'Become a Distributor',
            },
            {
              id: 'ctaSecondary',
              kind: 'text',
              label: 'Secondary button',
              default: 'Shop Products',
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Backdrop texture',
              defaultUrl:
                'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1600&q=40',
            },
          ],
        },
      ],
    },
    {
      id: 'company',
      label: 'Company',
      sections: [
        {
          id: 'hero',
          label: 'Page hero',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Who We Are' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Welcome to AGP' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'A direct-selling wellness company built on excellence, driven by results, and committed to transforming lives — one cup at a time.',
            },
          ],
        },
        {
          id: 'pillars',
          label: 'Pillars',
          fields: [
            {
              id: 'items',
              kind: 'objectList',
              label: 'Pillars',
              itemFields: [
                { id: 'num', kind: 'text', label: 'Number' },
                { id: 'title', kind: 'text', label: 'Title' },
                { id: 'body', kind: 'textarea', label: 'Body' },
              ],
              default: [
                {
                  num: '01',
                  title: '100% Natural',
                  body: 'Alpha Global Prestige is committed to using only the finest and most natural ingredients across our entire product range — from wellness beverages to personal care. Every formula is designed to work with your body, not against it.',
                },
                {
                  num: '02',
                  title: 'Healthy',
                  body: 'Our mission is to make healthy and nutritious living accessible to every family. Our beverages are infused with antioxidants, vitamins, and minerals — formulated with premium ingredients that benefit your whole family and help fight free radicals.',
                },
                {
                  num: '03',
                  title: 'Worldwide',
                  body: 'From its humble beginnings in the Philippines, AGP now supplies nationwide and meets global demand across Europe, the USA, Canada, Asia, and the Middle East — a testament to the trust our customers and partners have placed in us.',
                },
              ],
              maxItems: 6,
            },
          ],
        },
        {
          id: 'foundation',
          label: 'Our Foundation',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Our Foundation' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Built by Professionals.\nDriven by Purpose.',
            },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              default: [
                'Alpha Global Prestige is a direct-selling company built by professionals who believe in **Excellence**. The company offers quality natural products that promote a **Healthy** lifestyle and build **Wealth**.',
                'One of our core missions is to break the financial barriers in the lives of every Business Partner. We strive to promote quality living by providing a unique business opportunity with minimum investment.',
                'We are continuously adding life by sharing a cup — for over nine years and counting. For two consecutive years, Alpha Global Prestige has received multiple awards for the company, its management, and its products. We grew through word of mouth and repeat orders from clients who used our natural, healthy green coffee as part of their daily immune-boosting routine.',
                'Our satisfied clients became our resellers, distributors, and stockists — in the Philippines and across the globe. *Your success is our commitment.*',
              ],
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Foundation image',
              defaultUrl: 'https://images.unsplash.com/photo-1497366811353-6870744d04b2?w=900&q=80',
            },
          ],
        },
        {
          id: 'certs',
          label: 'Certifications',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Certifications & Recognition' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Certificate & Recognition' },
            {
              id: 'items',
              kind: 'objectList',
              label: 'Certificates',
              itemFields: [
                { id: 'type', kind: 'text', label: 'Type' },
                { id: 'name', kind: 'text', label: 'Name' },
                { id: 'detail', kind: 'textarea', label: 'Detail' },
              ],
              default: [
                {
                  type: 'Government Certification',
                  name: 'FDA License to Operate',
                  detail:
                    'Issued by the Food and Drug Administration of the Republic of the Philippines. Issued to AGP Klair International, Incorporated — San Carlos, Pangasinan. Valid through August 2024 (renewal on file).',
                },
                {
                  type: 'Religious Certification',
                  name: 'Halal Certificate',
                  detail:
                    'Certified by the Halal Development Institute of the Philippines. Issued to AGP Klair International, Incorporated — confirming products meet halal standards following comprehensive evaluation, inspection, and analysis.',
                },
                {
                  type: 'International Award',
                  name: 'Global Excellence Award 2019',
                  detail:
                    'Alpha Global Prestige recognized as Most Promising Global Opportunity Company. International recognition on world-class corporate and/or product-based global policies and quality. Presented at the 32nd Global Excellence Award.',
                },
                {
                  type: 'Consumer Award',
                  name: 'Most Trusted Consumer Brand',
                  detail:
                    'Awarded two consecutive years for outstanding consumer trust, product quality, and brand integrity. A testament to the genuine satisfaction of our customers and the quality of every AGP product.',
                },
                {
                  type: 'Brand Recognition',
                  name: 'Global Brand Award',
                  detail:
                    "International recognition affirming AGP's growing global presence, consistent product quality, and commitment to excellence in direct-selling wellness — across 7+ countries worldwide.",
                },
                {
                  type: 'Company Recognition',
                  name: 'Excellence in Management Award',
                  detail:
                    "Recognizing AGP's leadership team for outstanding business management, ethical practices, and dedication to building a sustainable and impactful wellness company across the Philippines and beyond.",
                },
              ],
              maxItems: 12,
            },
          ],
        },
      ],
    },
    {
      id: 'testimonials',
      label: 'Testimonials',
      sections: [
        {
          id: 'hero',
          label: 'Page hero',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'What Our Community Says' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Real Results.\nReal Stories.',
              hint: 'Line breaks are kept.',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Hear from the distributors, partners, and customers whose lives have been transformed by AGP.',
            },
          ],
        },
        {
          id: 'intro',
          label: 'Section band',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Our Community' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Trusted by Thousands' },
            {
              id: 'lead',
              kind: 'textarea',
              label: 'Lead paragraph',
              default:
                'From everyday customers to full-time business partners, AGP has touched lives across 7+ countries. Here are their stories.',
            },
          ],
        },
        {
          id: 'quotes',
          label: 'Quotes',
          // The hint lives on the SECTION, not the field: the editor renders a
          // hint for richParas fields only, so an objectList's own hint is
          // silently dropped.
          hint: 'Leave these on Default to keep using the testimonials from Storefront → Pages → About. Click Customize to manage them here instead, with a photo per person.',
          fields: [
            {
              id: 'items',
              kind: 'objectList',
              label: 'Testimonials',
              itemFields: [
                // MUST be named `imageKey` — that's what collectStorefrontImageKeys
                // allowlists, and an image under any other key can never be served
                // by GET /storefront/asset.
                { id: 'imageKey', kind: 'image', label: 'Photo' },
                { id: 'quote', kind: 'textarea', label: 'Quote' },
                { id: 'author', kind: 'text', label: 'Name' },
                { id: 'role', kind: 'text', label: 'Role / location' },
              ],
              // The handoff's six, verbatim. Photos are optional per row (these
              // have none), which is why the manifest lint skips empty imageKeys.
              default: [
                {
                  imageKey: '',
                  quote:
                    "I never imagined that one cup of Java Green Coffee would change my health and my business. I lost weight, my energy is back, and I've built a team of 20 distributors in just six months.",
                  author: 'Maria Santos',
                  role: 'Elite Distributor — Pangasinan, Philippines',
                },
                {
                  imageKey: '',
                  quote:
                    'AGP gave me a business I can be proud of. The products are genuinely good — my customers keep coming back. Two years in, this is now my full-time income.',
                  author: 'Ricardo Cruz',
                  role: 'Director-Level Partner — Dubai, UAE',
                },
                {
                  imageKey: '',
                  quote:
                    "I was skeptical at first, but the results spoke for themselves. My blood sugar stabilized, I have more energy, and I've introduced Choco Moringga Plus to my entire family.",
                  author: 'Grace Dela Torre',
                  role: 'Loyal Customer — Manila, Philippines',
                },
                {
                  imageKey: '',
                  quote:
                    'Being part of the AGP community in Canada has been a blessing. We share health, we share opportunity, and we share a cup — just as the brand promises.',
                  author: 'Jose Reyes',
                  role: 'AGP Stockist — Toronto, Canada',
                },
                {
                  imageKey: '',
                  quote:
                    "The support from the AGP team is second to none. They treat every partner like family. I started with the Builder Package and now I'm at Director level — in under a year.",
                  author: 'Lorna Aquino',
                  role: 'Director — Qatar',
                },
                {
                  imageKey: '',
                  quote:
                    'Java Green Coffee is now a daily ritual in my household. My husband and I both noticed the difference in just three weeks. We couldn’t imagine a morning without it.',
                  author: 'Ana Mercado',
                  role: 'Regular Customer — Cebu, Philippines',
                },
              ],
              maxItems: 24,
            },
          ],
        },
        {
          id: 'cta',
          label: 'Closing band',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Join the AGP Community' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Your Story Starts Here.' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Thousands of lives changed. Thousands of businesses built. The next success story could be yours.',
            },
            {
              id: 'ctaPrimary',
              kind: 'text',
              label: 'Primary button',
              default: 'Start Your Journey',
            },
            {
              id: 'ctaSecondary',
              kind: 'text',
              label: 'Secondary button',
              default: 'View Products',
            },
            {
              id: 'image',
              // Same defaultUrl as home.cta.image, and the page falls back to
              // whatever Home is actually using until this one is customized —
              // otherwise adding this field would silently revert a backdrop the
              // client had already uploaded on Home.
              kind: 'image',
              label: 'Backdrop texture',
              defaultUrl:
                'https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1600&q=40',
              hint: "Leave on Default to reuse the Home page's closing-band backdrop.",
            },
          ],
        },
      ],
    },
    {
      id: 'products',
      label: 'Products page',
      sections: [
        {
          id: 'hero',
          label: 'Hero band',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Our Catalogue' },
            { id: 'title', kind: 'text', label: 'Title', default: 'AGP Products' },
            {
              id: 'sub',
              kind: 'text',
              label: 'Subtitle',
              default: 'Award-winning wellness beverages, distributor packages & merchandise',
            },
            {
              id: 'image',
              kind: 'image',
              label: 'Hero band image',
              defaultUrl:
                'https://images.unsplash.com/photo-1471193945509-9ad0617afabf?w=1600&q=80',
            },
          ],
        },
      ],
    },
    {
      id: 'policies',
      label: 'Policies',
      sections: [
        {
          id: 'hero',
          label: 'Page hero',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Rules & Guidelines' },
            { id: 'title', kind: 'text', label: 'Title', default: 'AGP Policies' },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Please read the following policies carefully. These govern all transactions, shipments, and partnerships with Alpha Global Prestige.',
            },
          ],
        },
        {
          id: 'refund',
          label: '01 — Refund & Cancellation',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Refund & Cancellation Policy' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              hint: 'Rows starting with "- " render as bullets.',
              default: [
                'All products sold by Alpha Global Prestige are considered final sale. **Products once sold cannot be refunded.** If you have concerns regarding a product you have received, please contact us or your nearest AGP Business Partner as soon as possible to discuss available options.',
                'Product exchanges may be considered on a case-by-case basis, subject to the following conditions:',
                '- The request must be raised within 7 days of receipt.',
                '- The product must be unused, in its original sealed packaging, and in resalable condition.',
                '- All exchanges are subject to review and approval at the sole discretion of Alpha Global Prestige management.',
                'AGP reserves the right to decline any exchange request that does not meet these conditions.',
              ],
            },
          ],
        },
        {
          id: 'terms',
          label: '02 — Terms & Conditions',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Terms & Conditions' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              default: [
                "Once a Business Partner decides to purchase any of the available AGP packages, payment may be made via bank transfer or remittance to any of the company's designated accounts. Please allow **2–3 working days** for payment confirmation before the order is processed.",
                '**UAE & Middle East:** Packages may be collected directly from our UAE office upon confirmation of payment. Please coordinate with your assigned Business Partner or AGP representative for collection schedules.',
                '**Philippines:** Philippine packages may be collected from an authorized AGP distributor or stockist in your area. Alternatively, orders may be shipped via courier of your choice, subject to an additional shipping fee based on your location and package size.',
                'By proceeding with any purchase, the buyer acknowledges and agrees to these Terms and Conditions, as well as all applicable AGP distributor policies and the Memorandum of Agreement.',
              ],
            },
          ],
        },
        {
          id: 'shipment',
          label: '03 — Shipment',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Shipment Policy' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              default: [
                "Shipment of AGP products within the Philippines and the UAE may be arranged through any locally available courier service. Shipping charges are applicable and will vary depending on the consignee's address, total package weight, and cargo dimensions.",
                'AGP does not take responsibility for delays caused by the courier service, force majeure events, or incorrect shipping information provided by the buyer. Please ensure all delivery details are accurate at the time of ordering.',
                'For assistance with shipment arrangements, please contact us directly or reach out to your nearest AGP Business Partner. We will be glad to help coordinate delivery to ensure you receive your products safely and on time.',
              ],
            },
          ],
        },
        {
          id: 'privacy',
          label: '04 — Privacy & Data Use',
          fields: [
            { id: 'title', kind: 'text', label: 'Title', default: 'Privacy & Data Use' },
            {
              id: 'body',
              kind: 'richParas',
              label: 'Body',
              default: [
                'Alpha Global Prestige — Klair International is committed to protecting the privacy of our customers, business partners, and distributors. Any personal information collected through our website, application forms, or business transactions is used solely for the purpose of order processing, account management, and business communications.',
                'We do not sell, share, or disclose your personal information to any third party without your consent, except where required by Philippine law (Republic Act No. 10173 — Data Privacy Act of 2012) or as necessary to fulfill your order.',
                'For any privacy-related concerns or data removal requests, please contact us at agpmanagement7@gmail.com.',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'contact',
      label: 'Contact',
      sections: [
        {
          id: 'hero',
          label: 'Page hero',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Reach Out' },
            {
              id: 'title',
              kind: 'textarea',
              label: 'Title',
              default: 'Start a Conversation\nWith Us',
            },
            {
              id: 'sub',
              kind: 'textarea',
              label: 'Subtitle',
              default:
                'Whether you have questions about our products, want to become a distributor, or simply need help — we are here.',
            },
          ],
        },
        {
          id: 'form',
          label: 'Message form',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Send Us a Message' },
            { id: 'title', kind: 'text', label: 'Title', default: 'Questions or Inquiries?' },
            {
              id: 'lead',
              kind: 'textarea',
              label: 'Lead paragraph',
              default:
                'We are here to help you in the best way we can. Fill out the form and our team will get back to you within 1–2 business days.',
            },
            { id: 'sentTitle', kind: 'text', label: 'Sent — title', default: 'Message sent' },
            {
              id: 'sentBody',
              kind: 'textarea',
              label: 'Sent — body',
              default:
                'Thanks for reaching out — our team will get back to you within 1–2 business days.',
            },
          ],
        },
        {
          id: 'office',
          label: 'Office details',
          fields: [
            { id: 'label', kind: 'text', label: 'Label', default: 'Our Office' },
            { id: 'country', kind: 'text', label: 'Country heading', default: 'Philippines' },
            {
              id: 'address',
              kind: 'text',
              label: 'Address',
              default: 'San Carlos City, Pangasinan',
            },
            {
              id: 'addressLine2',
              kind: 'text',
              label: 'Address line 2',
              default: 'Republic of the Philippines',
            },
            { id: 'phone', kind: 'text', label: 'Phone', default: '09171189105' },
            { id: 'email', kind: 'text', label: 'Email', default: 'agpmanagement7@gmail.com' },
            {
              id: 'website',
              kind: 'text',
              label: 'Website',
              default: 'www.alphaglobal-prestige.com',
            },
            {
              id: 'hours',
              kind: 'stringList',
              label: 'Hours',
              default: ['Monday – Friday', '8:00 AM – 6:00 PM (PHT)'],
              maxItems: 6,
            },
          ],
        },
      ],
    },
    {
      id: 'chrome',
      label: 'Header',
      sections: [
        {
          id: 'topbar',
          label: 'Markets bar',
          fields: [
            {
              id: 'countries',
              kind: 'stringList',
              label: 'Markets',
              default: [
                'Philippines',
                'United States',
                'Canada',
                'Australia',
                'Japan',
                'Saipan',
                'Middle East',
              ],
              maxItems: 12,
            },
          ],
        },
      ],
    },
  ],
};
